import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { getActiveSeason, getSeasonState, getLastHeldSeason, getNextRegistration, getEventDateLabel } from '../config/seasons';
import { formatDay, eventWhen, venueText } from '../components/Season/SeasonFacts';
import OptimizedImage from '../components/OptimizedImage';
import { SEASON_2026 } from '../config/results2026';
import PhotoStrip from '../components/PhotoStrip';

// Import images

// Partner logos
import laPirouetteRoyale from '../assets/la_pirouette_royale_logo.png';
import ogataBalletSchool from '../assets/ogata_ballet_school_logo.png';
import theFifthLogo from '../assets/the_fifth_logo.png';

function computeCountdown(target, now = new Date()) {
  const diff = target - now;
  if (diff <= 0) return null;
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days:  Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    mins:  Math.floor((totalSeconds % 3600) / 60),
    secs:  totalSeconds % 60,
  };
}

/**
 * Big-tile countdown clock. Ticks every second. Renders four tiles
 * (DAYS · HOURS · MIN · SEC) with large gold numerals.
 *
 * Mobile-first sizing: tiles must fit 4-across on a 320px viewport without
 * wrapping. Total width budget at 320px is ~300px after container padding,
 * so each tile is at most ~70px wide including gaps. Colon separators are
 * hidden below the "sm" breakpoint (600px) to save horizontal space.
 */
const CountdownClock = ({ target }) => {
  const [now, setNow] = useState(new Date());
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 600 : false
  );

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const c = computeCountdown(target, now);
  if (!c) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--primary-gold)', fontSize: '1.5rem', fontWeight: 600 }}>
        The stage is live now.
      </div>
    );
  }

  const tiles = [
    { label: 'DAYS', value: c.days },
    { label: 'HRS',  value: String(c.hours).padStart(2, '0') },
    { label: 'MIN',  value: String(c.mins).padStart(2, '0')  },
    { label: 'SEC',  value: String(c.secs).padStart(2, '0')  },
  ];

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: isNarrow ? '0.35rem' : 'clamp(0.5rem, 2vw, 1.25rem)',
      flexWrap: 'nowrap',
      margin: '0 auto',
      maxWidth: '100%',
    }}>
      {tiles.map(({ label, value }, i) => (
        <React.Fragment key={label}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.92)',
            border: `${isNarrow ? 1 : 2}px solid var(--primary-gold)`,
            borderRadius: isNarrow ? '8px' : '12px',
            padding: isNarrow ? '0.55rem 0.35rem' : 'clamp(0.75rem, 2vw, 1.5rem) clamp(1rem, 3vw, 1.75rem)',
            minWidth: isNarrow ? '58px' : 'clamp(70px, 15vw, 110px)',
            flex: isNarrow ? '1 1 0' : undefined,
            boxShadow: isNarrow ? '0 4px 12px rgba(0, 0, 0, 0.12)' : '0 8px 24px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: isNarrow ? '1.75rem' : 'clamp(2rem, 6vw, 3.75rem)',
              fontWeight: 700,
              fontFamily: "'Playfair Display', 'Georgia', serif",
              color: 'var(--primary-gold)',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {value}
            </div>
            <div style={{
              marginTop: isNarrow ? '0.25rem' : '0.4rem',
              fontSize: isNarrow ? '0.6rem' : 'clamp(0.6rem, 1.2vw, 0.75rem)',
              letterSpacing: '0.12em',
              fontWeight: 600,
              color: 'var(--charcoal)',
            }}>
              {label}
            </div>
          </div>
          {!isNarrow && i < tiles.length - 1 && (
            <div style={{
              alignSelf: 'center',
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
              color: 'var(--primary-gold)',
              fontWeight: 700,
              lineHeight: 1,
            }} aria-hidden="true">
              :
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/** The biggest school discount this season offers, in percent (0 if none). */
const maxSchoolDiscount = (season) => Math.max(0, ...(season.schoolPricing?.tiers || []).map((t) => t.percentOff));

// What's new this season, with the key dates. Shown while the season is open
// or announced. Prices come from SEASONS so they match checkout.
const NewForSeason = ({ season }) => {
  const p = season.prices;
  const topSchoolDiscount = maxSchoolDiscount(season);
  const items = [
    ['Ensembles', 'Groups of three to eight dancers, judged as one. Perform alongside the dancers you train with, even across different schools.'],
    p && season.earlyBirdEndsAt && ['Early-Bird Pricing', `Registration is $${p.registrationEarlyBird} instead of $${p.registration} until ${formatDay(season.earlyBirdEndsAt)}.${
      season.comboEndsAt ? ` Pair a solo with an ensemble for $${p.combo} instead of $${p.solo + p.ensemble} until ${formatDay(season.comboEndsAt)}.` : ''}`],
    ['Pointe & Soft Shoes', 'Adult Recreational now has two divisions, so every dancer is judged alongside dancers in the same shoes.'],
    ['Schools & Studios', `Teachers register every student and ensemble in one place${topSchoolDiscount ? `, with studio pricing up to ${topSchoolDiscount}% off` : ''}.`],
  ].filter(Boolean);

  return (
    <section className="section" style={{ background: 'var(--light-gray)' }}>
      <div className="container" style={{ maxWidth: '52rem' }}>
        <h2 style={{ textAlign: 'center' }}>New for {season.year}</h2>
        <p style={{
          textAlign: 'center', fontSize: '1.1rem', color: 'var(--soft-gray)',
          maxWidth: '40rem', margin: '0 auto 2.5rem', lineHeight: 1.7,
        }}>
          This season brings new ways to take the stage.
        </p>

        <div>
          {items.map(([title, body]) => (
            <div key={title} style={{
              display: 'flex', flexWrap: 'wrap', gap: '0.4rem 2rem',
              padding: '1.4rem 0', borderTop: '1px solid rgba(201, 169, 110, 0.45)', textAlign: 'left',
            }}>
              <h3 style={{
                flex: '0 0 13rem', margin: 0, fontSize: '1.2rem', color: 'var(--primary-gold)', textAlign: 'left',
              }}>
                {title}
              </h3>
              <p style={{ flex: '1 1 18rem', margin: 0, lineHeight: 1.7, color: 'var(--charcoal)', textAlign: 'left' }}>
                {body}
              </p>
            </div>
          ))}
        </div>

        <p style={{
          textAlign: 'center', margin: '2rem 0 0', paddingTop: '1.5rem', fontStyle: 'italic', color: 'var(--soft-gray)',
          borderTop: '1px solid rgba(201, 169, 110, 0.45)',
        }}>
          Competition day: {eventWhen(season).replace(/^A /, 'a ')} · Venue: {venueText(season).replace(/^To /, 'to ')}
        </p>

        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <Link to="/register" className="cta-button" style={{ display: 'inline-block' }}>
            Register for {season.year}
          </Link>
          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/competition-rules" style={{ color: 'var(--primary-gold)', textDecoration: 'none', fontWeight: '600', fontSize: '1.1rem' }}>
              Read the {season.year} Rules &amp; Fees →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState('');

  const activeSeason = getActiveSeason();
  const seasonState = getSeasonState(activeSeason.year);
  const seasonOpen = seasonState === 'open';
  // Three very different pages: a season to sign up for (open, or announced
  // and about to open), dancers about to compete (pre_event), and nothing on
  // stage — the last season is over and the next isn't in SEASONS yet.
  const upcoming = seasonOpen || seasonState === 'coming_soon';
  const seasonPast = !upcoming && seasonState !== 'pre_event';
  // The season the wrap-up copy is about, and the registration to promote next.
  const heldSeason = getLastHeldSeason() || activeSeason;
  const nextRegistration = getNextRegistration();
  const nextOpensTitle = `${nextRegistration.year} Registration Opens ${nextRegistration.opensPhrase.replace(/^(in|on) /, '')}`;
  // The moment dancers take the stage, for the pre-event countdown.
  const stageDate = new Date(activeSeason.stageStartsAt || activeSeason.eventDate);
  const ctaLabel = upcoming ? `Register for ${activeSeason.year}` : 'Stay in the Loop';
  const ctaHref  = upcoming ? '/register' : '#newsletter';
  const topSchoolDiscount = maxSchoolDiscount(activeSeason);

  const heroSlides = upcoming ? [
    {
      type: 'image',
      slug: 'adult-rec-first-place-dancer-high-alasecond',
      alt: 'A dancer on stage at Prix du Canada',
      title: seasonOpen ? `Registration for ${activeSeason.year} Is Open` : `Prix du Canada ${activeSeason.year}`,
      subtitle: `${seasonOpen ? '' : `Registration opens ${nextRegistration.opensPhrase}. `}This year, ensembles join the stage. Perform alongside the dancers you train with, or shine on your own.${
        seasonOpen && activeSeason.earlyBirdEndsAt && new Date() <= new Date(activeSeason.earlyBirdEndsAt)
          ? ` Early-bird pricing until ${formatDay(activeSeason.earlyBirdEndsAt).replace(/, \d{4}$/, '')}.` : ''}`,
      cta: ctaLabel,
    },
    {
      type: 'image',
      slug: 'youngest-division-kids-smile-award-ceremony',
      alt: 'Young dancers at the Prix du Canada award ceremony',
      title: 'For Schools & Studios',
      subtitle: `Bring your whole studio to the stage. Register every student, solo and ensemble in one place${topSchoolDiscount ? `, with studio pricing up to ${topSchoolDiscount}% off` : ''}.`,
      cta: 'Register Your Studio',
      ctaHref: '/register/school',
    },
    {
      type: 'image',
      slug: 'judge-giving-a-few-words-to-junior-dancers',
      alt: 'A judge speaking with the junior dancers',
      title: 'Thank You for an Unforgettable First Year',
      subtitle: 'Our inaugural season took the stage on September 20, 2026 at the Sandra Faire and Ivan Fecan Theatre. To every dancer, teacher and family who made it happen — thank you.',
      cta: '2026 Results & Gallery',
      ctaHref: '/2026',
    },
  ] : seasonPast ? [
    {
      type: 'image',
      slug: 'judge-giving-a-few-words-to-junior-dancers',
      alt: 'A judge speaking with the junior dancers',
      title: `Thank You for ${heldSeason.year}`,
      subtitle: 'To every dancer, teacher and family who made it happen: thank you.',
      cta: ctaLabel,
    },
    {
      type: 'image',
      slug: 'youngest-division-kids-smile-award-ceremony',
      alt: 'Young dancers at the Prix du Canada award ceremony',
      title: 'Results & Judging Reports',
      subtitle: `Your report from the judging panel is ready. Log in to your dancer profile and open it under "Past Competitions".`,
      cta: 'Go to My Profile',
      ctaHref: '/profile',
    },
    {
      type: 'image',
      slug: 'adult-rec-first-place-dancer-high-alasecond',
      alt: 'An adult dancer on stage at Prix du Canada',
      title: nextOpensTitle,
      subtitle: 'Subscribe below and be the first to know.',
      cta: ctaLabel,
    },
  ] : [
    {
      type: 'image',
      slug: 'adult-rec-first-place-dancer-high-alasecond',
      alt: 'A dancer on stage at Prix du Canada',
      title: `Prix du Canada ${activeSeason.year}`,
      subtitle: `See you on ${getEventDateLabel(activeSeason.year) || 'competition day'}${activeSeason.venue ? ` at the ${activeSeason.venue}` : ''}.`,
      showCountdown: true,
      cta: ctaLabel,
    },
    {
      type: 'image',
      slug: 'youngest-division-kids-smile-award-ceremony',
      alt: 'Young dancers at the Prix du Canada award ceremony',
      title: 'Come Cheer Them On',
      subtitle: 'The dancers have trained all year. Now they need an audience! Day passes are $20 at the door. Bring the whole crew.',
      cta: 'View Schedule',
      ctaHref: '/competition#schedule',
    },
    {
      type: 'image',
      slug: 'empty-stage-with-pdc-logo-projection',
      alt: 'The Prix du Canada stage before the competition',
      title: nextOpensTitle,
      subtitle: 'Subscribe below and be the first to know.',
      cta: ctaLabel,
    },
  ];

  // Auto-advance slides every 9 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3); // 3 slides total
    }, 9000);
    
    return () => clearInterval(timer);
  }, []);
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % 3); // 3 slides total
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + 3) % 3);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail }),
      });
      setNewsletterStatus(response.ok ? 'submitted' : 'error');
      if (response.ok) setNewsletterEmail('');
    } catch {
      setNewsletterStatus('error');
    }
    setNewsletterSubmitting(false);
  };

  return (
    <Layout>
      {/* Hero Section with Slideshow */}
      <section className="hero-section" style={{ position: 'relative' }}>
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: currentSlide === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              pointerEvents: currentSlide === index ? 'auto' : 'none'
            }}
          >
            <div className="hero-background">
              {slide.type === 'image' ? (
                slide.slug ? (
                  // Competition photography, served at the width the device
                  // actually needs. A full-bleed hero is the single heaviest
                  // request on the page, so it is worth not shipping a 2MB
                  // stock JPEG to a phone.
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={[640, 1280, 1920].map((w) => `/photos/${slide.slug}-${w}.webp ${w}w`).join(', ')}
                      sizes="100vw"
                    />
                    <img
                      src={`/photos/${slide.slug}-1920.jpg`}
                      srcSet={[640, 1280, 1920].map((w) => `/photos/${slide.slug}-${w}.jpg ${w}w`).join(', ')}
                      sizes="100vw"
                      alt={slide.alt}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fetchpriority={index === 0 ? 'high' : undefined}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </picture>
                ) : (
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )
              ) : null}
            </div>
            <div className="hero-content" style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: '1000px',
              textAlign: 'center',
              zIndex: 3
            }}>
              <h1 style={{ 
                textAlign: 'center',
              }}>
                {slide.title}
              </h1>
              <p style={{
                textAlign: 'center',
                margin: '0 auto 1.5rem auto',
              }}>
                {slide.subtitle}
              </p>
              {slide.showCountdown && (
                <div style={{ margin: '0 auto 2rem' }}>
                  <CountdownClock target={stageDate} />
                </div>
              )}
              {(() => {
                // Per-slide CTA overrides the global one (used for the
                // "View Schedule" slide in the closed-season slideshow).
                const href = slide.ctaHref || ctaHref;
                const isInternal = href.startsWith('/');
                return isInternal ? (
                  <Link
                    to={href}
                    className="cta-button"
                    style={{ margin: '0 auto', display: 'inline-block' }}
                  >
                    {slide.cta}
                  </Link>
                ) : (
                  <a
                    href={href}
                    className="cta-button"
                    style={{ margin: '0 auto', display: 'inline-block' }}
                  >
                    {slide.cta}
                  </a>
                );
              })()}
            </div>
          </div>
        ))}
        
        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          style={{
            position: 'absolute',
            left: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(212, 165, 116, 0.9)',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            cursor: 'pointer',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(196, 149, 99, 1)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(212, 165, 116, 0.9)'}
          aria-label="Previous slide"
        >
          ‹
        </button>
        <button
          onClick={nextSlide}
          style={{
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(212, 165, 116, 0.9)',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            cursor: 'pointer',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(196, 149, 99, 1)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(212, 165, 116, 0.9)'}
          aria-label="Next slide"
        >
          ›
        </button>
        
        {/* Slide Indicators */}
        <div style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '10px',
          zIndex: 10
        }}>
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: currentSlide === index ? '40px' : '12px',
                height: '12px',
                borderRadius: '6px',
                background: currentSlide === index ? 'var(--primary-gold)' : 'rgba(255, 255, 255, 0.5)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {upcoming && <NewForSeason season={activeSeason} />}

      {/* 2026 season band — the community photographs have nowhere else to
          live, and they are the most persuasive evidence of what the day is
          actually like. Doubles as the route into the archive. */}
      <section className="section" style={{ background: 'var(--warm-white)' }}>
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>Our 2026 Season</h2>
          <p style={{
            textAlign: 'center', fontSize: '1.1rem', color: 'var(--soft-gray)',
            maxWidth: '44rem', margin: '0 auto 2.5rem', lineHeight: 1.7,
          }}>
            {SEASON_2026.dancers} dancers took the stage in our first season, from age seven to
            adult — cheered on by the teachers, families and fellow competitors who came out to
            support them.
          </p>

          <PhotoStrip
            photos={[
              ['adult-dancers-gather-backstage-before-curtain', 'Dancers gathered backstage before the curtain'],
              ['friends-come-to-support-adult-dancer', 'Friends supporting an adult competitor'],
              ['poeple-chatting-in-lobby-after-comp', 'Dancers and families in the lobby after the competition'],
              ['kids-rehersing-in-classroom-for-ceremony', 'Young dancers rehearsing before the ceremony'],
            ]}
          />

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/2026" style={{
              display: 'inline-block', padding: '0.8rem 2rem',
              background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
              color: 'white', textDecoration: 'none', borderRadius: '6px', fontWeight: 600,
            }}>
              2026 Results &amp; Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2>Where Every Dancer Shines</h2>
              <p>Prix du Canada is revolutionizing ballet competitions across the country. We've created something extraordinary—a professional platform where every single dancer gets their moment on stage, not just the top few. From Canada's most prestigious venues to Canada's most supportive community, we're turning your dance milestones into shining diamonds.</p>
              <p>Forget competition stress and elimination rounds. Here, your artistry is celebrated, your moment in the spotlight is guaranteed, and your dedication receives the professional recognition it deserves.</p>
            </div>
            <div className="about-image">
              <OptimizedImage
                slug="adult-competitive-with-nice-portdebra"
                alt="A dancer performing at Prix du Canada"
                sizes="(max-width: 900px) 100vw, 45vw"
                rounded
              />
            </div>
          </div>

          <div className="mission-vision">
            <div className="card" style={{textAlign: 'center'}}>
              <h3>Every Dancer Performs</h3>
              <p style={{textAlign: 'center'}}>No eliminations, no cuts, no disappointments. Every registered dancer takes our professional stage with live audience applause. Your artistry deserves recognition—and you'll get it.</p>
            </div>
            <div className="card" style={{textAlign: 'center'}}>
              <h3>Professional Excellence</h3>
              <p style={{textAlign: 'center'}}>Distinguished judges, premium Toronto venues, optional personalized feedback, and performance standards that rival international competitions. This is your chance to experience what it means to be a professional artist.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Competition Divisions */}
      <section className="divisions-section">
        <div className="container">
          <h2 style={{textAlign: 'center'}}>Your Stage Awaits</h2>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: 'var(--soft-gray)', maxWidth: '800px', margin: '0 auto 2rem'}}>Prix du Canada celebrates Canadian dancers of all ages and gender identities. From young beginners taking their first bow to seasoned artists sharing their passion—everyone deserves their moment in the spotlight.</p>

          <div className="divisions-grid">
            <Link to="/competition" style={{textDecoration: 'none', color: 'inherit'}}>
              <div className="division-card">
                <img
                  src="/photos/young-dancer-with-super-cute-smile-1280.jpg"
                  srcSet={`/photos/young-dancer-with-super-cute-smile-640.jpg 640w, /photos/young-dancer-with-super-cute-smile-1280.jpg 1280w`}
                  sizes="(max-width: 900px) 100vw, 33vw"
                  alt="Young Artist divisions"
                  loading="lazy"
                />
                <div className="division-overlay">
                  <h3>Young Artist - 2 Divisions</h3>
                  <p>Ages 7–11 • Where dreams begin</p>
                </div>
              </div>
            </Link>
            <Link to="/competition" style={{textDecoration: 'none', color: 'inherit'}}>
              <div className="division-card">
                <img
                  src="/photos/junior-dancer-beautiful-penche-1280.jpg"
                  srcSet={`/photos/junior-dancer-beautiful-penche-640.jpg 640w, /photos/junior-dancer-beautiful-penche-1280.jpg 1280w`}
                  sizes="(max-width: 900px) 100vw, 33vw"
                  alt="Rising Star divisions"
                  loading="lazy"
                />
                <div className="division-overlay">
                  <h3>Rising Star - 2 Divisions</h3>
                  <p>Ages 12–17 • Excellence & artistry</p>
                </div>
              </div>
            </Link>
            <Link to="/competition" style={{textDecoration: 'none', color: 'inherit'}}>
              <div className="division-card">
                <img
                  src="/photos/adult-rec-dancer-on-pointe-arabesque-1280.jpg"
                  srcSet={`/photos/adult-rec-dancer-on-pointe-arabesque-640.jpg 640w, /photos/adult-rec-dancer-on-pointe-arabesque-1280.jpg 1280w`}
                  sizes="(max-width: 900px) 100vw, 33vw"
                  alt="Lifelong Dancer divisions"
                  loading="lazy"
                />
                <div className="division-overlay">
                  <h3>Lifelong Dancer - 3 Divisions</h3>
                  <p>Adults • Passion knows no age</p>
                </div>
              </div>
            </Link>
          </div>

          <div style={{textAlign: 'center', marginTop: '3rem'}}>
            <p style={{fontSize: '1.1rem', color: 'var(--soft-gray)', marginBottom: '1.5rem', textAlign: 'center'}}>Seven solo divisions • Ensembles • Professional feedback</p>
            <Link to="/competition" style={{color: 'var(--primary-gold)', textDecoration: 'none', fontWeight: '600', fontSize: '1.1rem'}}>
              Explore All Divisions & Timeline →
            </Link>
          </div>
        </div>
      </section>

      {/* Between seasons: after the stage, or the two weeks before it.
          While a season is open or announced, "New for <year>" takes this role. */}
      {!upcoming && (
      <section className="section" style={{background: 'var(--light-gray)'}}>
        <div className="container">
          <h2 style={{textAlign: 'center'}}>{seasonPast ? `That's a Wrap on ${heldSeason.year}` : 'Two Weeks to Showtime'}</h2>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: 'var(--soft-gray)', maxWidth: '700px', margin: '0 auto 3rem'}}>
            {seasonPast
              ? `Every dancer who took the stage should be proud. ${nextRegistration.year} is already on the way.`
              : `Registration for ${activeSeason.year} has closed and our dancers are getting ready to take the stage. Come be part of the moment.`}
          </p>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2.5rem', maxWidth: '800px', margin: '0 auto'}}>
            <div className="card" style={{textAlign: 'center', borderTop: '4px solid var(--primary-gold)', padding: '2.5rem 2rem'}}>
              <div style={{width: '80px', height: '80px', background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', borderRadius: '50%', margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <span style={{color: 'white', fontSize: '2rem', fontWeight: 'bold'}}>{seasonPast ? '🏆' : '👏'}</span>
              </div>
              <h3 style={{color: 'var(--primary-gold)', marginBottom: '1rem'}}>{seasonPast ? 'Your Results' : 'Support the Dancers'}</h3>
              <p style={{textAlign: 'center'}}>
                {seasonPast
                  ? 'Your judging report is ready. Log in to your dancer profile and open it under "Past Competitions". We\'ll email you when your performance video is ready.'
                  : 'Day passes are $20 at the door, no ticket needed in advance. Bring your family, your friends, your whole studio!'}
              </p>
            </div>

            <div className="card" style={{textAlign: 'center', borderTop: '4px solid var(--deep-gold)', padding: '2.5rem 2rem', background: 'var(--soft-cream)'}}>
              <div style={{width: '80px', height: '80px', background: 'linear-gradient(135deg, var(--deep-gold), var(--primary-gold))', borderRadius: '50%', margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <span style={{color: 'white', fontSize: '2rem', fontWeight: 'bold'}}>📅</span>
              </div>
              <h3 style={{color: 'var(--deep-gold)', marginBottom: '1rem'}}>Stay Tuned for Next Year</h3>
              <p style={{textAlign: 'center'}}>Registration for the next season opens <strong>{nextRegistration.opensPhrase}</strong>. Subscribe below to hear first.</p>
            </div>
          </div>

          <div style={{textAlign: 'center', marginTop: '3rem'}}>
            <Link to="/competition-rules" style={{color: 'var(--primary-gold)', textDecoration: 'none', fontWeight: '600', fontSize: '1.1rem'}}>
              View the Rules &amp; Fees →
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Community Partners Section */}
      {/* Section backgrounds alternate. The timeline section is hidden while a
          season is open or announced, so the last two swap colours then. */}
      <section className="section" style={{background: upcoming ? 'var(--light-gray)' : 'white', textAlign: 'center', paddingTop: '3.5rem', paddingBottom: '3.5rem'}}>
        <div className="container">
          <h2 style={{margin: '0 0 2rem 0', textAlign: 'center'}}>Our Partnering Studios</h2>
          <p style={{fontSize: '1.05rem', color: 'var(--soft-gray)', maxWidth: '960px', margin: '0 auto 3rem', lineHeight: 1.65, textAlign: 'center'}}>
            Proud to collaborate with these exceptional studios in shaping the next generation of Canadian dancers.
          </p>

          {(() => {
            const partners = [
              { name: 'La Pirouette Royale', href: 'https://lapirouetteroyale.com/', logo: laPirouetteRoyale, height: 160 },
              { name: 'Ogata Ballet School', href: 'https://www.ogataballetschool.ca/', logo: ogataBalletSchool, height: 155 },
              { name: 'The Fifth Dance', href: 'https://www.thefifthdance.com/', logo: theFifthLogo, height: 135 }
            ];
            return (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'clamp(2rem, 6vw, 5rem)',
                maxWidth: '1000px',
                margin: '0 auto'
              }}>
                {partners.map((p) => (
                  <a
                    key={p.name}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit ${p.name} (opens in new tab)`}
                    title={`Visit ${p.name}`}
                    style={{
                      display: 'inline-block',
                      textDecoration: 'none',
                      transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.35s ease',
                      filter: 'drop-shadow(0 6px 14px rgba(44, 44, 44, 0.14))',
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.08) translateY(-4px)';
                      e.currentTarget.style.filter = 'drop-shadow(0 12px 24px rgba(44, 44, 44, 0.22))';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1) translateY(0)';
                      e.currentTarget.style.filter = 'drop-shadow(0 6px 14px rgba(44, 44, 44, 0.14))';
                    }}
                    onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1) translateY(0)'; }}
                  >
                    <img
                      src={p.logo}
                      alt={p.name}
                      style={{
                        height: `${p.height}px`,
                        width: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        background: 'transparent'
                      }}
                    />
                  </a>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="section" style={{background: upcoming ? 'white' : 'var(--light-gray)', textAlign: 'center'}}>
        <div className="container">
          <h2>Stay in the Loop</h2>
          <p style={{fontSize: '1.2rem', color: 'var(--soft-gray)', maxWidth: '600px', margin: '0 auto 2rem', textAlign: 'center'}}>
            Subscribe to our newsletter for updates, exclusive offers, and priority access to workshops and masterclasses.
          </p>
          <form onSubmit={handleNewsletterSubmit} style={{display: 'flex', gap: '0.75rem', maxWidth: '480px', margin: '0 auto', flexWrap: 'wrap', justifyContent: 'center'}}>
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="Your email address"
              required
              disabled={newsletterStatus === 'submitted'}
              style={{flex: '1', minWidth: '240px', padding: '0.85rem 1.25rem', border: '2px solid var(--primary-gold)', borderRadius: '6px', fontSize: '1rem', outline: 'none', opacity: newsletterStatus === 'submitted' ? 0.6 : 1}}
            />
            <button
              type="submit"
              disabled={newsletterSubmitting || newsletterStatus === 'submitted'}
              className="cta-button"
              style={{
                padding: '0.85rem 2rem',
                fontSize: '1rem',
                border: 'none',
                cursor: (newsletterSubmitting || newsletterStatus === 'submitted') ? 'default' : 'pointer',
                opacity: newsletterSubmitting ? 0.7 : 1,
                transition: 'background 0.3s ease, color 0.3s ease',
                ...(newsletterStatus === 'submitted' ? {
                  background: '#28a745',
                  color: 'white'
                } : {})
              }}
            >
              {newsletterStatus === 'submitted' ? '✓ Subscribed' : newsletterSubmitting ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
          {newsletterStatus === 'error' && (
            <p style={{color: '#dc3545', marginTop: '0.75rem', fontSize: '0.95rem'}}>Something went wrong. Please try again.</p>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Home;
